/**
 * S14's four significance states, drawn rather than typed.
 *
 * ## 🔴 Why these are SVG and not the spec's characters
 *
 * `UI-Design-Spec.md` writes the four states as `⧗ ✓ ⚠ –`, and until now the screen rendered those
 * characters literally. **Measured 2026-08-05, in the running app: three of the four are not in
 * Geist.** Rendering each into a canvas against the page's own family and against a family that
 * certainly does not exist gives identical advance widths for `⧗` (U+29D7), `✓` (U+2713) and `⚠`
 * (U+26A0) — 9px, 11.98px and 13.78px in both — which is what font substitution looks like from
 * the outside. Only the dashes are really Geist's.
 *
 * So the highest-value element on the screen was being drawn by whatever the operating system
 * happened to substitute. Three consequences, all of them real:
 *
 * - **On this box that is Segoe UI Symbol, whose U+29D7 is a filled hourglass.** At the 13px the
 *   run was set in, in `--text-tertiary`, it reads as a smudge — indistinguishable from the `–` of
 *   *not ranked*, which is the one distinction `⧗` exists to make.
 * - **It differs per operating system.** A Mac substitutes Apple Symbols, a bare Linux box draws a
 *   tofu box. The §8 reading cannot depend on which machine is looking at it.
 * - **The four marks had four different optical weights and widths**, because they came from
 *   different fonts, so the run did not align as a run.
 *
 * ⚠ **This is a rendering change and not a change to the vocabulary.** The four states, their
 * meanings and §8's `(✓)` notation for an isolated pass are unchanged; `title` still carries the
 * words. What changes is that the marks are now geometry this repository controls, on one optical
 * grid, at a size the run can actually be read at.
 *
 * The characters are kept in `aria-label` so a screen reader and a copied cell still say what the
 * spec says.
 */

import type { JSX } from 'react';

/** The four states, plus the fifth rendering §8 requires: a pass with no passing neighbour. */
export type Mark = 'pass' | 'isolated' | 'too_early' | 'flagged' | 'not_ranked' | 'unresolved';

/**
 * What each mark is, in words. Doubles as the accessible name, so the spec's own character sits
 * beside the sentence rather than being the whole of it.
 */
const MEANING: Readonly<Record<Mark, string>> = {
  pass: '✓ promotable',
  isolated: '(✓) promotable, but isolated — a non-result under §8',
  too_early: '⧗ too early — below the pre-registered minimum',
  flagged: '⚠ flagged — diverging from its own backtest',
  not_ranked: '– not ranked',
  unresolved: '[✓] promotable — but the venue and Box 1 decisions come first (D73)',
};

/**
 * The geometry, on one 24×24 grid.
 *
 * ⚠ **`too_early` is a ring and `not_ranked` is a dash, and that pairing is the point.** These are
 * the two states the old rendering could not keep apart. A ring reads as *open, still filling*; a
 * dash reads as *nothing to say*. They share no strokes, so no substitution and no size can
 * collapse one into the other.
 *
 * `isolated` draws §8's parentheses as arcs, at the same stroke weight as everything else. The
 * check inside is deliberately smaller than a bare `pass`: an isolated pass should read as *less*
 * than a pass before its colour is even considered, because colour is the first thing a
 * colour-blind reader loses.
 */
function path(mark: Mark): JSX.Element {
  switch (mark) {
    case 'pass':
      return <path d="M4.5 12.6 L9.8 17.9 L19.5 6.4" />;
    case 'isolated':
      // ⚠ **The arcs sit at 5.6/18.4 with a 9-unit radius, and the first attempt at 8.2/15.8 with
      // 11.5 was wrong.** Rendered at 13px in the family list, parentheses that close that tightly
      // around the check read as a *ring* — which is the mark for `too_early`, the one state this
      // notation must never be confused with. Pushing them out and curving them harder makes them
      // read as brackets, and the inner check is kept small so an isolated pass looks like *less*
      // than a pass before its colour is considered at all.
      return (
        <>
          <path d="M6.4 3.4 A 9 9 0 0 0 6.4 20.6" />
          <path d="M17.6 3.4 A 9 9 0 0 1 17.6 20.6" />
          <path d="M9.3 12.3 L11.4 14.4 L14.8 9.2" />
        </>
      );
    case 'too_early':
      return <circle cx="12" cy="12" r="7.6" />;
    case 'flagged':
      return (
        <>
          <path d="M12 3.8 L21.4 20 L2.6 20 Z" />
          <path d="M12 9.6 L12 14.2" />
          <path d="M12 17 L12 17.2" />
        </>
      );
    case 'unresolved':
      // 🔴 **A CHECK inside a square — and it used to be a dash, which is the D73 reversal
      // drawn.** Under §4 a modelled short could never be promoted, so this mark enclosed the
      // `not_ranked` dash to say *"unranked by rule"*. D73 makes shorts promotable, so the enclosed
      // glyph is now the check: the candidate **is** eligible for the G1–G12 gate, and what the
      // boundary adds is that two decisions — the venue and Box 1 — come before it runs.
      //
      // ⚠ **A square, not a circle or a triangle**, because those are `too_early` and `flagged`,
      // and §7's rule is that the marks must not be separable by colour alone — so no two may share
      // their strokes.
      //
      // ⚠ **Measured against `isolated`, which also encloses a check.** Both are qualified passes,
      // so the enclosure had to be distinguishable at the 13px the run is set in: hard-cornered box
      // versus outward-curving arcs, and the check inside this one is full size while `isolated`'s
      // is deliberately shrunk. Verified by rendering the two side by side rather than by assuming
      // it — the arcs-versus-ring mistake in `isolated`'s own comment is the precedent.
      //
      // ⚠ **Not colour.** The accent is never a state or a mode, so the caveat is carried by
      // geometry and by the label — never by a hue.
      return (
        <>
          <rect x="2.4" y="2.4" width="19.2" height="19.2" rx="1.2" />
          <path d="M6.8 12.4 L10.4 16 L17.2 7.6" />
        </>
      );
    default:
      return <path d="M5 12 L19 12" />;
  }
}

/**
 * One significance mark.
 *
 * `tone` is passed in rather than derived from `mark`, because the two are genuinely independent:
 * a family's *best* state is drained of colour when every pass it holds is an isolated one, and
 * that is a fact about its neighbours rather than about the mark. `FleetScreen.toneOf` owns it.
 */
export function SigMark({
  mark,
  tone,
  title,
}: {
  mark: Mark;
  tone: string;
  title?: string;
}): JSX.Element {
  return (
    <span className="jmt-mark" data-tone={tone} title={title ?? MEANING[mark]}>
      <svg
        viewBox="0 0 24 24"
        width="1em"
        height="1em"
        fill="none"
        stroke="currentColor"
        strokeWidth={mark === 'isolated' ? 1.9 : 2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
        role="img"
        aria-label={MEANING[mark]}
      >
        {path(mark)}
      </svg>
    </span>
  );
}

/**
 * A non-running deployment state, as a silhouette — S14 task 4.9.
 *
 * ## 🔴 Why the `State` column became a mark on the candidate cell
 *
 * `StatePill` rendered one of four words in a 115px column, and on the live fleet **every visible
 * row reads `RUNNING`** — a column spending an eighth of the table's width to repeat the same word
 * down its whole length. The information is *"this one is not running"*, which is one bit, and one
 * bit does not need a column. Task 4.9 recovered all 115px and moved the bit onto the identity
 * cell, where the exception now sits beside the name it qualifies.
 *
 * ⚠ **`RUNNING` draws NOTHING, and that is the design rather than a gap.** A mark on every row is
 * the column again in a narrower hat. The mark means *"read this row differently"*, so drawing one
 * for the normal case would spend the ink and say nothing.
 *
 * 🔴 **No two of these share their strokes — with each other OR with the six `SigMark`s.** §7's
 * rule is that marks must not be separable by colour alone, and these sit two cells away from a
 * significance mark in the same row at the same 13px. So: pause bars for halted, a hard square for
 * stopped, a dashed ring with a hand for starting, a narrowing stack for draining. ⚠ The dashed ring
 * is deliberately not `too_early`'s solid ring — dashed reads as *provisional*, and the hand inside
 * it is a clock, which is the one thing `starting` and `too_early` genuinely have in common.
 *
 * ⚠ **Drained of colour, always.** The state of a deployment is not a result, and the accent can
 * never mean money. `--text-tertiary` carries it, the same ink the meta line beside it uses.
 */
export function StateMark({ state }: { state: string }): JSX.Element | null {
  const word = state.toLowerCase();
  if (word === 'running') return null;
  return (
    <span className="jmt-state-mark" title={`${word} — not accumulating evidence`}>
      <svg
        viewBox="0 0 24 24"
        width="1em"
        height="1em"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        role="img"
        aria-label={`${word} — not accumulating evidence`}
      >
        {statePath(word)}
      </svg>
    </span>
  );
}

/** The geometry, on the same 24×24 grid as the significance marks. */
function statePath(word: string): JSX.Element {
  switch (word) {
    case 'halted':
      return (
        <>
          <path d="M8 5 L8 19" />
          <path d="M16 5 L16 19" />
        </>
      );
    case 'stopped':
      return <rect x="5.5" y="5.5" width="13" height="13" rx="1" />;
    case 'starting':
      return (
        <>
          <circle cx="12" cy="12" r="8.5" strokeDasharray="3 3" />
          <path d="M12 4 L12 12 L18 15" />
        </>
      );
    case 'draining':
      // A stack that narrows downwards: what is left is being let out. Shares no stroke with the
      // pause bars, which is the pair most at risk of being read as one another.
      return (
        <>
          <path d="M5 8 L19 8" />
          <path d="M8 13 L16 13" />
          <path d="M11 18 L13 18" />
        </>
      );
    default:
      // A state the server learned and this screen has not. Visible, deliberately unranked — the
      // same choice `exitReasonFill` makes for an unknown reason.
      return <circle cx="12" cy="12" r="2.5" />;
  }
}

/** The four the legend lists, in the order it lists them. */
export const DEPLOYMENT_STATE_MARKS: ReadonlyArray<string> = [
  'halted',
  'draining',
  'starting',
  'stopped',
];

/**
 * The significance state a candidate is in, as a mark. `supported` decides the fifth and
 * `executionUnresolved` the sixth.
 *
 * 🔴 **`flagged` still outranks everything, and that order is deliberate.** A divergence past 2σ
 * is a *bug* signal rather than a result — `Fleet-Selection-Rule.md` calls it exactly that — and it
 * is worth seeing on a short mirror too, because those are backtestable (unlike recipe 8). Hiding a
 * bug behind any other label would be the worse trade.
 *
 * 🔴 **`executionUnresolved` no longer outranks anything — it QUALIFIES `promotable` — and that
 * is D73 in one line.** Under §4 it short-circuited every state, because a modelled short could
 * never be promoted. It can now: `promotable` means *"eligible for the unchanged G1–G12 gate"*,
 * never *"gets real money"*. So a short below the minimums reads `too_early` (the progress is real),
 * one that lost to its own control reads `not_ranked` (it lost), and only a short that has actually
 * earned the gate carries the boundary.
 *
 * ⚠ **`isolated` wins over `unresolved` when both apply**, because §8 calls an isolated pass a
 * *non-result* — a stronger claim than *"decisions pending"*, and one that does not go away when
 * the decisions are taken.
 */
export function markFor(
  significance: string,
  supported: boolean,
  executionUnresolved = false,
): Mark {
  if (significance === 'flagged') return 'flagged';
  switch (significance) {
    case 'promotable':
      if (!supported) return 'isolated';
      return executionUnresolved ? 'unresolved' : 'pass';
    case 'too_early':
      return 'too_early';
    default:
      return 'not_ranked';
  }
}

/**
 * `positions.exit_reason`'s vocabulary in the order S14 reads it, with the words the screen uses.
 *
 * ⚠ **Mirrors `jmt_api.store.fleet.EXIT_REASONS`, and the order is load-bearing** — most common
 * first, which is also least-alarming first. A breakdown that led with `manual` would put the rarest
 * cause at the top of the one list an operator scans to answer "what is closing these".
 *
 * `take_profit` is hyphenated for the screen and underscored on the wire, which is the only place
 * the two vocabularies differ.
 */
export const EXIT_REASON_LABELS: ReadonlyArray<readonly [key: string, label: string]> = [
  ['signal', 'signal'],
  ['stop', 'stop'],
  ['take_profit', 'take-profit'],
  ['risk_engine', 'risk engine'],
  ['manual', 'manual'],
];

/**
 * The breakdown as ordered `label · count` pairs, dropping reasons that did not happen.
 *
 * 🔴 **Returns `[]` when nothing has closed, and the caller renders `—` rather than a row of
 * zeroes.** Five zeroes is five measurements that were never taken, and this cohort spent its first
 * bar with exactly that — 67 positions open and none closed.
 *
 * ⚠ An unrecognised key is kept rather than dropped. The vocabulary is closed today, so a key not in
 * the list means the server learned a new one and the screen is the last place that should hide it.
 */
export function closeBreakdown(
  byReason: Readonly<Record<string, number>>,
): Array<{ key: string; label: string; count: number }> {
  const out: Array<{ key: string; label: string; count: number }> = [];
  const known = new Set<string>();
  for (const [key, label] of EXIT_REASON_LABELS) {
    known.add(key);
    const count = byReason[key] ?? 0;
    if (count > 0) out.push({ key, label, count });
  }
  for (const [key, count] of Object.entries(byReason)) {
    if (!known.has(key) && count > 0) out.push({ key, label: key, count });
  }
  return out;
}

/**
 * The fill for one exit reason in the distribution bar.
 *
 * 🔴 **Only two reasons get a hue, and they get the money ones because that is what they mean.**
 * `colors.css` is explicit that hue in this product is reserved: the accent *"can never be mistaken
 * for one that means money"*, `--live` amber is *"never text, never a fill, never an icon"*, and
 * `--attn-tint` is *"the footer stale-data strip, and nothing else"*. A take-profit **is** a win
 * realised and a stop **is** a loss cut, so `--profit-dim` and `--loss-dim` are not decoration here —
 * and `semantic.css` names *"distribution bars"* as exactly what the `-dim` pair is for.
 *
 * ⚠ **The rest separate by luminance, not hue**, which is the register's own move: three neutral
 * steps that stay distinguishable because each token is themed, so the ramp inverts with the canvas
 * instead of breaking on light. ⚠ `--text-tertiary` carries a rule — *"labels-at-12px-and-up only,
 * never a value"* — which governs text; this is a fill, and the number it belongs to is rendered
 * separately in `--text-secondary`.
 */
export function exitReasonFill(key: string): string {
  switch (key) {
    case 'take_profit':
      return 'var(--profit-dim)';
    case 'stop':
      return 'var(--loss-dim)';
    case 'signal':
      // The majority reason on almost every candidate, so it takes the most visible neutral.
      return 'var(--text-tertiary)';
    case 'risk_engine':
      return 'var(--border-strong)';
    case 'manual':
      return 'var(--text-disabled)';
    default:
      // A reason the server learned and this screen has not. Visible, deliberately unranked.
      return 'var(--border-grid)';
  }
}
